/** Small pieces shared by the OCEAN views. */
import React, { useEffect, useState } from 'react';
import { Icon } from './icons';
import type { CompanyMini, Kind, ProblemItem, SourceInfo, TechMini } from './api';

export const fmt = (n: number | null | undefined): string => (n ?? 0).toLocaleString('en-US');

export const KIND_LABEL: Record<Kind, string> = { problem: 'Problem', company: 'Company', solution: 'COSMOS' };

/** "Source content: X days old" — the PRD's wording, honest about how the age
 *  was found (see Backend/ocean/build_index.py, "source date"). `floorLabel`
 *  is the month the source is known to have existed by ("June 2026"). */
export function sourceAge(s: SourceInfo | null | undefined, floorLabel = 'June 2026'): { text: string; title: string } {
  if (!s || s.age_days == null) return { text: 'Source date unknown', title: '' };
  const n = fmt(s.age_days);
  switch (s.precision) {
    case 'year':
      return { text: `~${n} days old`, title: `Estimated from the year of the source (${s.year}); the exact publish date is unknown.` };
    case 'capture':
      return { text: `${n}+ days old`, title: `Published on or before ${floorLabel}; the exact date is unknown.` };
    case 'month':
      return { text: `${n} days old`, title: `Dated by the month in the source's web address.` };
    default:
      return { text: `${n} days old`, title: s.date ? `Published ${s.date}` : '' };
  }
}

export function useIsMobile(query = '(max-width: 860px)'): boolean {
  const get = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false);
  const [m, setM] = useState(get);
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia(query);
    const on = () => setM(mq.matches);
    on();
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on); };
  }, [query]);
  return m;
}

/** picker: the click opens the collection picker (search results) instead of removing (Saved lists). */
export const SaveIcon: React.FC<{
  saved: boolean; kind: Kind; collection?: string | null; onClick: (e: React.MouseEvent) => void; size?: number;
  picker?: boolean;
}> = ({ saved, kind, collection, onClick, size = 18, picker }) => {
  const where = collection ? ` in ${collection}` : '';
  const label = saved ? (picker ? `Saved${where} — move or remove` : `Saved${where} — click to remove`)
    : (picker ? `Save ${KIND_LABEL[kind]} Card to a collection` : `Save ${KIND_LABEL[kind]} Card`);
  return (
    <button type="button" className="oc-save" aria-pressed={saved} title={label} aria-label={label}
            onClick={e => { e.stopPropagation(); onClick(e); }}>
      <Icon name={saved ? 'bookmarkFilled' : 'bookmark'} size={size} />
    </button>
  );
};

export const Skeleton: React.FC<{ rows?: number }> = ({ rows = 3 }) => (
  <div className="oc-list" aria-hidden="true">
    {Array.from({ length: rows }, (_, i) => (
      <div className="oc-skel" key={i}><i style={{ width: '82%' }} /><i style={{ width: '46%' }} /><i style={{ width: '30%' }} /></div>
    ))}
  </div>
);

/** Previous / next, as Cosmos pages its results ("← Prev  Page 2 / 9  Next →"). */
export const Pager: React.FC<{ page: number; pages: number; onPage: (p: number) => void; label: string; busy?: boolean }> =
  ({ page, pages, onPage, label, busy }) => {
    if (pages <= 1) return null;
    const btn = 'px-3 py-1.5 text-sm border border-grayscale-300 rounded disabled:opacity-50 hover:bg-grayscale-50';
    return (
      <nav className="mt-3 pt-3 flex items-center justify-center gap-3" aria-label={`${label} pages`}>
        <button type="button" className={btn} disabled={page <= 1 || busy} onClick={() => onPage(page - 1)}>← Prev</button>
        <span className="text-sm text-grayscale-1200">
          Page <span className="font-semibold">{fmt(page)}</span> / {fmt(pages)}
        </span>
        <button type="button" className={btn} disabled={page >= pages || busy} onClick={() => onPage(page + 1)}>Next →</button>
      </nav>
    );
  };

/** "Relevance: NN%", as Cosmos shows it on a technology. Only for scores that
 *  mean something on their own (the engine's 0-100): a keyword match's number
 *  is relative to the best one in the list, so it is not shown. */
export const RelevancePill: React.FC<{ relevance?: number }> = ({ relevance }) =>
  typeof relevance === 'number' && isFinite(relevance)
    ? <span className="oc-relpill">Relevance: {Math.max(0, Math.min(100, Math.round(relevance)))}%</span>
    : null;

/** A card's list is short (product tracker, 2026-10-29): at most this many in each. */
export const CARD_LIST_MAX = 5;

/** One clickable line in a card's "Related ..." / "Companies with ..." / "Relevant IPs ..." list. */
export const ProblemLinks: React.FC<{ items: ProblemItem[]; onOpen: (id: string) => void; empty: string; showCompany?: boolean;
                                        bordered?: boolean; max?: number }> =
  ({ items, onOpen, empty, showCompany = true, bordered = false, max }) => items.length ? (
    <ul className={bordered ? 'oc-dlist' : undefined}>
      {items.slice(0, max ?? items.length).map(p => (
        <li key={p.id}>
          <button type="button" className="oc-lnk" onClick={() => onOpen(p.id)}>
            {p.statement}
            {p.match && <RelevancePill relevance={p.relevance} />}
            {showCompany && <small>{[p.company?.name, p.country].filter(Boolean).join(' · ')}</small>}
          </button>
        </li>
      ))}
    </ul>
  ) : <div className="oc-none">{empty}</div>;

export const CompanyLinks: React.FC<{ items: CompanyMini[]; onOpen: (id: string, label: string) => void; empty: string;
                                        bordered?: boolean; max?: number }> =
  ({ items, onOpen, empty, bordered = false, max }) => items.length ? (
    <ul className={bordered ? 'oc-dlist' : undefined}>
      {items.slice(0, max ?? items.length).map(c => (
        <li key={c.id}>
          <button type="button" className="oc-lnk" onClick={() => onOpen(c.id, c.name)}
                  title={c.example ? `Similar problem: ${c.example}` : undefined}>
            {c.name}
            <small>{[c.industry, c.country].filter(Boolean).join(' · ') || '—'}</small>
          </button>
        </li>
      ))}
    </ul>
  ) : <div className="oc-none">{empty}</div>;

/** onOpen gets the engine's relevance for a scored match (the Cosmos card
 *  shows it), nothing for a keyword one. */
export const SolutionLinks: React.FC<{ items: TechMini[]; onOpen: (id: string, label: string, relevance?: number) => void;
                                         empty: string; bordered?: boolean; max?: number }> =
  ({ items, onOpen, empty, bordered = false, max }) => items.length ? (
    <ul className={bordered ? 'oc-dlist' : undefined}>
      {items.slice(0, max ?? items.length).map(t => (
        <li key={t.id}>
          <button type="button" className="oc-lnk" onClick={() => onOpen(t.id, t.title, t.match ? t.relevance : undefined)}>
            {t.title}
            {t.match && <RelevancePill relevance={t.relevance} />}
            <small>{[t.university, t.country, t.trl ? `TRL ${t.trl}` : null].filter(Boolean).join(' · ')}</small>
          </button>
        </li>
      ))}
    </ul>
  ) : <div className="oc-none">{empty}</div>;
